import dxValidator from 'devextreme/ui/validator';
import messageLocalization from 'devextreme/common/core/localization/message';

/**
 * App-wide: every DevExtreme validator (dx-validator, dx-form items) shows its broken rule as
 * a line of text below the field (.gpd-field-error, src/styles/forms.css) instead of
 * DevExtreme's tooltip, which only appears while the field is focused and flips above it -
 * over the label - when there is no room below. Red border and save block stay as they are.
 *
 * Opt out by putting the field inside `.gpd-no-inline-validation`. Grid/tree-list cells and the
 * multi-step popup (which validates every 200ms in the background) are left on the tooltip.
 */
const EXCLUDED = '.gpd-no-inline-validation, .dx-datagrid, .dx-treelist, .msp-step-pane, .role-step';

const ALERT_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">' +
  '<path d="M13.9248 21H10.0752C5.44476 21 3.12955 21 2.27636 19.4939C1.42317 17.9879 2.60736 15.9914 4.97574 11.9985L6.90057 8.75333C9.17559 4.91778 10.3131 3 12 3C13.6869 3 14.8244 4.91777 17.0994 8.75332L19.0243 11.9985C21.3926 15.9914 22.5768 17.9879 21.7236 19.4939C20.8704 21 18.5552 21 13.9248 21Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
  '<path d="M12 17V12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
  '<path d="M12 8.99828V8.98828" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>' +
  '</svg>';

const errorElements = new WeakMap<HTMLElement, HTMLElement>();

export function installInlineValidationErrors(): void {
  dxValidator.defaultOptions({ options: { onValidated: showInlineError } as any });
}

export function showInlineError(e: { element: any; component?: any; isValid: boolean; brokenRule?: any; name?: string }): void {
  const editor: HTMLElement | undefined = e.element?.get ? e.element.get(0) : e.element;
  if (!editor || editor.closest(EXCLUDED)) {
    return;
  }

  const message = e.isValid ? null : messageFor(e.brokenRule, e.name);
  let error = errorElements.get(editor);

  if (!message) {
    error?.remove();
    errorElements.delete(editor);
    return;
  }

  editor.classList.add('gpd-inline-validation');
  if (!error) {
    error = document.createElement('div');
    error.className = 'gpd-field-error';
    error.setAttribute('role', 'alert');
    error.innerHTML = ALERT_ICON + '<span></span>';
    errorElements.set(editor, error);
    // The text sits next to the editor, not inside it: when the editor is destroyed (e.g. an
    // *ngIf on the editor itself) take the text with it, or it lingers under a field that is gone.
    e.component?.on?.('disposing', () => {
      errorElements.get(editor)?.remove();
      errorElements.delete(editor);
    });
  }
  error.querySelector('span')!.textContent = message;

  const anchor = anchorFor(editor);
  if (error.previousElementSibling !== anchor) {
    anchor.after(error);
  }
}

/** DevExtreme's own required text ("Required" / "<name> is required") reads poorly - use ours. */
function messageFor(rule: any, name?: string): string | null {
  if (!rule) {
    return null;
  }
  if (rule.type === 'required') {
    const defaults = [
      messageLocalization.format('validation-required'),
      name ? (messageLocalization as any).format('validation-required-formatted', name) : null,
    ];
    if (!rule.message || defaults.includes(rule.message)) {
      return $localize`:@@fieldIsRequired:This field is required`;
    }
  }
  return rule.message ?? null;
}

/** A field's own wrapper - the text never goes above it. */
const FIELD_BOUNDARY = '.dx-field, .gpd-form-field, .dx-field-item, .dx-field-item-content';

/**
 * The text goes right after the field. If the field shares a row with something else (e.g. a
 * date box next to a delete button), go up to that row so the text lands below it, not beside it -
 * but never past the field's own wrapper, so it can't end up far away from its field.
 */
function anchorFor(editor: HTMLElement): HTMLElement {
  let anchor = editor;
  while (
    anchor.parentElement &&
    !anchor.parentElement.matches(FIELD_BOUNDARY) &&
    isRow(anchor.parentElement)
  ) {
    anchor = anchor.parentElement;
  }
  return anchor;
}

function isRow(el: HTMLElement): boolean {
  const style = getComputedStyle(el);
  return (style.display === 'flex' || style.display === 'inline-flex') && style.flexDirection.startsWith('row')
    || style.display === 'grid' && el.children.length > 1 && style.gridAutoFlow.startsWith('column');
}
